import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
//icon
import { AiFillCloseCircle, AiFillPlusCircle } from 'react-icons/ai';
//components
import Input from 'components/backoffice/customInput';

const MerchandisingForm = ({ control, merchandising, setValue }) => {
  const { t } = useTranslation(['backoffice']);
  const [options, setOptions] = useState(
    Array.isArray(merchandising?.options)
      ? merchandising.options
      : [{ title: '', subOptions: [] }]
  );
  const [subOptionTexts, setSubOptionTexts] = useState(['']);

  const updateOptionsInForm = (newOptions) => {
    setOptions(newOptions);
    setValue('options', newOptions);
  };

  const addOption = () => {
    const allFilled = options.every(opt => opt.title.trim() !== '');
    if (allFilled) {
      const updatedOptions = [...options, { title: '', subOptions: [] }];
      updateOptionsInForm(updatedOptions);
      setSubOptionTexts(prev => [...prev, '']);
    } else {
      toast.error(`${t('error')}: ${t('completeTitles')}`, { theme: 'colored' });
    }
  };

  const handleOptionChange = (index, field, value) => {
    const updatedOptions = [...options];
    updatedOptions[index][field] = value;
    updateOptionsInForm(updatedOptions);
  };

  const removeOption = (index) => {
    if (index === 0) {
      const updatedOptions = [...options];
      updatedOptions.splice(index, 1);
      updateOptionsInForm(updatedOptions);
      setSubOptionTexts(['']);
    } else {
      const updatedOptions = [...options];
      updatedOptions.splice(index, 1);
      updateOptionsInForm(updatedOptions);
      const updatedSubOptionTexts = [...subOptionTexts];
      updatedSubOptionTexts.splice(index, 1);
      setSubOptionTexts(updatedSubOptionTexts);
    }
  };

  const addSubOption = (index) => {
    const updatedOptions = [...options];
    const subOptionText = subOptionTexts[index].trim();
    if (subOptionText !== '') {
      updatedOptions[index].subOptions.push(subOptionText);
      updateOptionsInForm(updatedOptions);
      setValue(`options[${index}].subOptions`, updatedOptions[index].subOptions);
      setSubOptionTexts(prev => {
        const newTexts = [...prev];
        newTexts[index] = ''; 
        return newTexts;
      });
    } else {
      toast.error(`${t('error')}: ${t('subOptionEmpty')}`, { theme: 'colored' });
    }
  };

  const removeSubOption = (optionIndex, subOptionIndex) => {
    const updatedOptions = [...options];
    updatedOptions[optionIndex].subOptions.splice(subOptionIndex, 1);
    updateOptionsInForm(updatedOptions);
    setValue(`options[${optionIndex}].subOptions`, updatedOptions[optionIndex].subOptions);
    const updatedSubOptionTexts = [...subOptionTexts];
    updatedSubOptionTexts[optionIndex] = ''; 
    setSubOptionTexts(updatedSubOptionTexts);
  };

  return (
    <Row className="align-items-center mt-3">
      <Col sm={6} className='d-flex align-items-center justify-content-start mb-2'>
        <Form.Label className='mb-0 me-2'>{t('product.options')}</Form.Label>
      </Col>
      <Col sm={6} className='d-flex align-items-center justify-content-end'>
        <Button
          variant="link"
          className="bg-transparent p-0"
          disabled={options.every(opt => opt.title.trim() === '')}
        >
          <AiFillPlusCircle
            size={35}
            color="#0d6efd"
            className="cursor-pointer"
            onClick={addOption}
            title={`${t('add')} ${t('product.options')}`}
          />
        </Button>
      </Col>
      <Form.Group className="mb-3">
        {options.map((opt, index) => (
          <React.Fragment key={index}>
            <Row className='mt-4'>
              <Col sm={5} className='d-flex align-items-center justify-content-between'>
                <Form.Label>{t('titulo')}</Form.Label>
              </Col>
              <Col sm={6} className='d-flex align-items-center justify-content-between px-0'>
                <Form.Label>{t('product.options')}</Form.Label>
              </Col>
            </Row>
            <Row className="d-flex align-items-start justify-content-start">
              <Col sm={5}>
                <Controller
                  control={control}
                  name={`options[${index}].title`}
                  defaultValue={opt.title}
                  render={({ field: { onChange } }) => (
                    <Input
                      placeholder={t('titulo')}
                      type="text"
                      value={opt.title}
                      onChange={(e) => {
                        onChange(e);
                        handleOptionChange(index, 'title', e);
                      }}
                    />
                  )}
                />
              </Col>
              <Col sm={6}>
                <div className='w-100'>
                  <Input
                    placeholder={t('product.writeAndPressEnter')}
                    type="text"
                    value={subOptionTexts[index]}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addSubOption(index);
                      }
                    }}
                    onChange={(e) => {
                      const newTexts = [...subOptionTexts];
                      newTexts[index] = e;
                      setSubOptionTexts(newTexts);
                    }}
                  />
                  {opt?.subOptions.length > 0 && (
                    <div className='d-flex flex-wrap mt-3'>
                      {opt?.subOptions.map((subOption, subIndex) => (
                        <span key={subIndex} className="badge suboption-span me-2 p-2 position-relative">
                          <span onClick={() => removeSubOption(index, subIndex)}>X</span>
                          {subOption}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Col>
              {(index !== 0 || opt.title.trim() !== '') && (
                <Col sm={1} className="d-flex align-items-end justify-content-end gap-1">
                  <AiFillCloseCircle
                    size={35}
                    color="#D9534F"
                    className="cursor-pointer"
                    title={`${t('delete')} ${t('options')}`}
                    onClick={() => removeOption(index)}
                  />
                </Col>
              )}
            </Row>
          </React.Fragment>
        ))}
      </Form.Group>
    </Row>
  );
};

MerchandisingForm.propTypes = {
  control: PropTypes.any,
  merchandising: PropTypes.any,
};

export default MerchandisingForm;